import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface SuicidalQuestionnaireModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientId: number;
    userId: number;
    token: string;
    initialData: {
        q1: boolean;
        q2: boolean;
        q3: boolean;
        q4: boolean;
    };
    onSuccess?: () => void;
}

export const SuicidalQuestionnaireModal: React.FC<SuicidalQuestionnaireModalProps> = ({
    isOpen,
    onClose,
    patientId,
    userId,
    token,
    initialData,
    onSuccess,
}) => {
    const [q1, setQ1] = useState(initialData.q1);
    const [q2, setQ2] = useState(initialData.q2);
    const [q3, setQ3] = useState(initialData.q3);
    const [q4, setQ4] = useState(initialData.q4);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Update state when initialData changes
    useEffect(() => {
        setQ1(initialData.q1);
        setQ2(initialData.q2);
        setQ3(initialData.q3);
        setQ4(initialData.q4);
    }, [initialData]);

    if (!isOpen) return null;

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);

        try {
            await apiService.updatePatientSuicidalQuestionnaire({
                token,
                patient_id: patientId,
                user_id: userId,
                q1,
                q2,
                q3,
                q4,
            });

            onSuccess?.();
            onClose();
        } catch (err: any) {
            console.error('Failed to update suicidal questionnaire:', err);
            setError(err.message || 'Failed to update questionnaire');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };

    return (
        <ModalWrapper modalId="SuicidalQuestionnaireModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Update Suicidal Questions"
            isLoading={isSaving}
            width="!max-w-2xl"
        >
            {/* Content */}
            <div className="px-6 pb-4">
                <div className="bg-[#EBF5F5] rounded px-4 py-4 space-y-3">
                        {/* Question 1 */}
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={q1}
                                onChange={(e) => setQ1(e.target.checked)}
                                disabled={isSaving}
                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380] disabled:opacity-50"
                            />
                            <span className="text-sm text-[#3a7380] font-medium">Death wish in recent weeks</span>
                        </div>

                        {/* Question 2 */}
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={q2}
                                onChange={(e) => setQ2(e.target.checked)}
                                disabled={isSaving}
                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380] disabled:opacity-50"
                            />
                            <span className="text-sm text-[#3a7380] font-medium">Thoughts or plans to commit suicide in recent weeks</span>
                        </div>

                        {/* Question 3 */}
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={q3}
                                onChange={(e) => setQ3(e.target.checked)}
                                disabled={isSaving}
                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380] disabled:opacity-50"
                            />
                            <span className="text-sm text-[#3a7380] font-medium">In the past, have tried self harm or to commit suicide</span>
                        </div>

                        {/* Question 4 */}
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={q4}
                                onChange={(e) => setQ4(e.target.checked)}
                                disabled={isSaving}
                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380] disabled:opacity-50"
                            />
                            <span className="text-sm text-[#3a7380] font-medium">At present, have thoughts of committing suicide</span>
                        </div>
                    </div>

                {/* Error message */}
                {error && (
                    <div className="mt-3 px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 pt-4">
                <div className="flex items-center justify-center gap-4">
                    <button
                        onClick={handleClose}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        {isSaving ? 'Saving...' : 'Update'}
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
